// Deliberately the raw core singleton, NOT `Amplify` from 'aws-amplify'. The
// root export is `DefaultAmplify`, whose `.configure()` unconditionally wires a
// Cognito Identity-Pool credentials provider
// (`CognitoAWSCredentialsAndIdentityIdProvider` + the `cognitoIdentity` AWS
// client). This app configures no `identityPoolId` and never reads
// `session.credentials` / `session.identityId`, so that code was bundled but
// dead at runtime. Importing the core singleton and wiring the token provider
// by hand (see `configureAmplify`) keeps behavior identical and drops the
// identity-pool graph from the shared chunk.
import { Amplify } from '@aws-amplify/core';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  fetchMFAPreference,
  getCurrentUser,
  resetPassword,
  setUpTOTP,
  signUp,
  updateMFAPreference,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage, Hub } from 'aws-amplify/utils';
import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import ToasterComponent from 'src/components/Shared/ToasterComponent';
import { getBaseUrl } from 'src/helper/api';
// Import the boot guard from the dependency-free leaf module — NOT from
// `useUserProfile`, which imports `useAuth` from here and would form an
// AuthContext ↔ useUserProfile import cycle (breaks Jest module init with a TDZ
// ReferenceError on AuthContext).
import { clearAllChatDrafts } from 'src/helper/chatDraftStore';
import { logger } from 'src/helper/logger';
import type { UserPoolConfig } from 'src/helper/shared-types';
import { getAccessToken } from 'src/helper/utils';
import { clearStoredViewConfigs } from 'src/helper/viewConfigStore';
import { useAppDispatch } from 'src/hooks/redux-hooks';
import { resetUserProfileBootGuard } from 'src/hooks/userProfileBootGuard';
import { authCheckSignedUrl, authForgotPassword, authResetPassword } from 'src/redux/auth/api';
import {
  fetchConfigDetails,
  fetchProviderConfigDetails,
  updateTemplate,
} from 'src/redux/profile/api';
import type { ClientAuthBootstrap } from 'src/shared/auth/clientAuthBootstrap';
import { checkVendorDomain } from 'src/shared/utils/hosts';
import { sessionRedirect } from 'src/shared/utils/sessionRedirect';
import { clearAllVendorBannerSessionLatches } from 'src/shared/utils/vendorWelcomeBanner';
import { resetWorkTypeDiagnosticsThrottle } from 'src/shared/utils/workTypeDiagnostics';

interface CognitoUser {
  username?: string;
  attributes?: {
    email?: string;
    email_verified?: boolean;
    name?: string;
    phone_number?: string;
    phone_number_verified?: boolean;
    sub?: string;
  };
  preferredMFA?: string;
  challengeName?: string;
  signInUserSession?: {
    accessToken?: {
      jwtToken?: string;
    };
  };
}
interface UseAuth {
  isRequesting: boolean;
  signIn: (username: string, password: string) => Promise<Result>;
  signOut: () => Promise<{ success: boolean; message: string }>;
  signOutAndClearOnly: () => Promise<{ success: boolean; message: string }>;
  setNewPassword: (newPassword: string) => Promise<Result>;
  confirmEmailVerification: (code: string, userId: string) => Promise<Result>;
  forgotPassword: (email: string) => Promise<Result>;
  forgotPasswordSubmit: (email: string, code: string, password: string) => Promise<Result>;
  authConfigDetails: UserPoolConfig;
  isAuthInitialized: boolean;
  isSigningIn: boolean;
  checkAuth: () => Promise<{ isAuthenticated: boolean; email: string | null }>;
  mfaVerification: (code: string, mfaType: string) => Promise<Result>;
  setupMfa: () => Promise<Result>;
  verifyTOTP: (code: string) => Promise<Result>;
  getCurrentMFA: () => Promise<Result>;
  triggerThemeChange: (name: string, template: string) => void;
  signup: (username: string, password: string, name: string) => Promise<Result>;
  domainNotValid: boolean;
  forgotPasswordAPI: (email: string) => Promise<Result>;
  checkSignedUrlAPI: (verifyId: string, expires: number, signature: string) => Promise<Result>;
  resetPasswordAPI: (
    password: string,
    verifyId: string,
    expires: number,
    signature: string,
  ) => Promise<Result>;
}

interface Result {
  success: boolean;
  message: string;
  navigate?: string;
  code?: string;
  username?: string;
  result?: unknown;
  // APX-738 — set only when the API answered 429 (rate limited).
  rateLimited?: boolean;
  // Seconds until the limit resets, from the 429 body's `retry_after`; null when absent.
  retryAfter?: number | null;
}

interface AwsConfigAuthType {
  region: string;
  authDomain: string;
  userPoolId: string;
  userPoolWebClientId: string;
  cookieStorage?: {
    domain: string;
    path?: string;
    expires?: number;
    sameSite?: string;
    secure?: boolean;
  };
  authenticationFlowType?: string;
}

type Props = {
  children?: React.ReactNode;
  /** COM-858 — see {@link useProvideAuth}. */
  initialAuthConfig?: ClientAuthBootstrap | null;
};

// Shape of the Amplify sign-in / confirm-sign-in output we actually read.
interface SignInResult {
  isSignedIn?: boolean;
  nextStep?: {
    signInStep?: string;
  };
}

// The subset of a caught Cognito / API error this context reads. Errors arrive
// as `unknown`; `toAuthError` narrows them to this shape without asserting `any`.
interface CaughtAuthError {
  name?: string;
  message?: string;
  status?: number | string;
  code?: string;
  // 429 body field from the API's rate limiter (seconds, or null for an HTTP-date Retry-After).
  retry_after?: unknown;
}

const toAuthError = (error: unknown): CaughtAuthError =>
  error && typeof error === 'object' ? (error as CaughtAuthError) : {};

// Standard failure Result for the many auth flows that simply surface the
// caught error's message (or a per-flow fallback). Centralizes the repeated
// `{ success: false, message: toAuthError(error).message || '…' }` return.
const authFailure = (error: unknown, fallback: string): Result => ({
  success: false,
  message: toAuthError(error).message || fallback,
});

export const AuthContext = createContext({} as UseAuth);

/**
 * @param initialAuthConfig COM-858 — Cognito fields the `(coordinator)` layout resolved on the
 * server. When present, Amplify is configured at hydration instead of after this hook's own
 * `api/public/config` round-trip, taking that hop off the front of the boot chain. The fetch
 * still runs in the background for the org-editable fields (theme `template`, `providers`,
 * `organization_uuid`). Absent → pre-COM-858 behaviour, unchanged.
 */
export const useProvideAuth = (initialAuthConfig?: ClientAuthBootstrap | null): UseAuth => {
  const [isRequesting, setIsRequesting] = useState(true);
  const [authConfigDetails, setAuthConfigDetails] = useState({} as UserPoolConfig);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [isSigningIn, setISigningIn] = useState(false);
  const [tempUserData, setTempUserData] = useState({} as CognitoUser);
  const [theme, setTheme] = useState('');
  const [domainNotValid, setDomainNotValid] = useState(false);

  const dispatch = useAppDispatch();

  // COM-858 — true once Amplify has been configured from the server-supplied bootstrap. The
  // background config fetch must then neither call `Amplify.configure()` again (that would
  // re-install the token provider/storage while the boot fan-out is in flight) nor flag the
  // domain as invalid on a failure: the app is already correctly configured.
  const configuredFromServerRef = useRef(false);

  const configureAmplify = useCallback((data: AwsConfigAuthType) => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

    const authConfig = {
      Cognito: {
        userPoolId: data?.userPoolId,
        userPoolClientId: data?.userPoolWebClientId,
        loginWith: {
          oauth: {
            domain: data?.authDomain,
            redirectSignIn: [`https://${hostname}`],
            redirectSignOut: [`https://${hostname}/signin`],
            responseType: 'code' as const,
            scopes: ['email', 'openid', 'profile', 'aws.cognito.signin.user.admin', 'phone'],
          },
        },
      },
    };

    // Both provider calls must happen BEFORE `Amplify.configure()`, mirroring
    // what `DefaultAmplify.configure()` does internally:
    //
    //   setAuthConfig      — the token store and orchestrator read the pool id /
    //                        client id from here. Without it `getTokens()` cannot
    //                        locate stored tokens or run a refresh, so every
    //                        authenticated request 401s.
    //   setKeyValueStorage — tokens live in cookies (the RSC seam in
    //                        `src/server/auth/session.ts` reads them server-side),
    //                        not the `defaultStorage` localStorage that
    //                        `DefaultAmplify` would have installed. Installing it
    //                        first also means the OAuth redirect listener that
    //                        `Amplify.configure()` notifies writes SSO tokens
    //                        straight to cookies.
    cognitoUserPoolsTokenProvider.setAuthConfig(authConfig);
    cognitoUserPoolsTokenProvider.setKeyValueStorage(
      new CookieStorage({
        domain: hostname,
        path: '/',
        expires: 1,
        sameSite: 'strict',
        secure: process.env.NODE_ENV === 'production',
      }),
    );

    // No `credentialsProvider` — this app has no identity pool. `AuthClass`
    // reads it as `this.authOptions?.credentialsProvider?.…`, so omitting it
    // simply leaves `session.credentials` / `session.identityId` undefined,
    // which nothing in the app reads.
    Amplify.configure(
      { Auth: authConfig },
      { Auth: { tokenProvider: cognitoUserPoolsTokenProvider } },
    );
    setIsAuthInitialized(true);
  }, []);

  const getAWSConfigDetails = useCallback(async () => {
    if (typeof window === 'undefined') return;
    const hostName = window.location.hostname;
    const payload = {
      client_key: hostName,
      ...(window.location.pathname?.includes('provider') ? { client_type: 'provider' } : {}),
    };
    if (hostName) {
      const isVendor = checkVendorDomain();
      if (isVendor) {
        dispatch(fetchProviderConfigDetails()(payload))
          .unwrap()
          .then((res) => {
            if (res && !configuredFromServerRef.current) {
              configureAmplify({
                authDomain: res?.auth_domain,
                region: res?.region,
                userPoolId: res?.user_pool_id,
                userPoolWebClientId: res?.client_id,
              });
            }
            setAuthConfigDetails(res);
            setTheme(res.template);
            setIsRequesting(false);
          })
          .catch((err) => {
            setIsRequesting(false);
            if (configuredFromServerRef.current) return;
            if (err?.status && Number(err?.status) < 500) {
              setDomainNotValid(true);
            } else if (err?.status && Number(err?.status) >= 500) {
              toast(
                <ToasterComponent
                  type="error"
                  icon="error"
                  description={err?.message || 'Something Went Wrong'}
                />,
              );
            }
          });
      } else {
        dispatch(fetchConfigDetails()(payload))
          .unwrap()
          .then((res) => {
            if (res && !configuredFromServerRef.current) {
              configureAmplify({
                authDomain: res?.auth_domain,
                region: res?.region,
                userPoolId: res?.user_pool_id,
                userPoolWebClientId: res?.client_id,
              });
            }
            setAuthConfigDetails(res);
            setTheme(res.template);
            setIsRequesting(false);
          })
          .catch((err) => {
            setIsRequesting(false);
            if (configuredFromServerRef.current) return;
            if (err?.status && Number(err?.status) < 500) {
              setDomainNotValid(true);
            } else if (err?.status && Number(err?.status) >= 500) {
              toast(
                <ToasterComponent
                  type="error"
                  icon="error"
                  description={err?.message || 'Something Went Wrong'}
                />,
              );
            }
          });
      }
    }
  }, [dispatch, configureAmplify]);

  // Initially we requesting for the user pool details from the backend,
  // and then we configure the amplify with the user pool details.
  //
  // COM-858 — when the server already resolved the Cognito fields, configure from those
  // (synchronously, so `isAuthInitialized` is true on the first commit). Same guards as the
  // fetch itself: never on the vendor portal (different pool, different endpoint) and never on
  // a `provider` path, where the client sends `client_type=provider` and may get a different
  // client id than the server resolved.
  //
  // COM-864 — and then skip the client `api/public/config` fetch entirely. Only the
  // `(coordinator)` layout supplies `initialAuthConfig`, and nothing there reads the rest of
  // the response: `providers` and `organization_uuid` feed the login pages (which get no
  // server config, so they still fetch), and `template` has no styles behind it (no
  // `body.<template>` CSS; the theme picker has no callers). The body class keeps coming from
  // `localStorage.template` below. Without the server config — auth/public pages, vendor
  // portal, `provider` paths, or a null/timed-out bootstrap — the fetch runs exactly as before.
  useLayoutEffect(() => {
    if (!isAuthInitialized) {
      if (
        initialAuthConfig &&
        !configuredFromServerRef.current &&
        typeof window !== 'undefined' &&
        !checkVendorDomain() &&
        !window.location.pathname?.includes('provider')
      ) {
        configuredFromServerRef.current = true;
        configureAmplify(initialAuthConfig);
        // Nothing is left to request; `isRequesting` means "config still in flight".
        setIsRequesting(false);
        return;
      }
      getAWSConfigDetails();
    }
  }, [configureAmplify, getAWSConfigDetails, initialAuthConfig, isAuthInitialized]);

  const fetchCurrentUser = useCallback(async (loadingState: boolean) => {
    try {
      // In v6, we need to use getCurrentUser and fetchAuthSession
      const user = await getCurrentUser();
      const session = await fetchAuthSession();

      const result = {
        username: user.username,
        userId: user.userId,
        signInUserSession: {
          accessToken: {
            jwtToken: session.tokens?.accessToken?.toString(),
          },
        },
        attributes: session.tokens?.idToken?.payload,
      };

      if (process.env.NODE_ENV === 'development') {
        localStorage.setItem('_jwt', JSON.stringify(result));
      }
      if (loadingState) {
        setIsRequesting(false);
      }
      return result;
    } catch (error) {
      if (loadingState) {
        setIsRequesting(false);
      }
      return error;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const session = await fetchAuthSession();

      return {
        isAuthenticated: !!session.tokens?.accessToken,
        email: (session.tokens?.idToken?.payload?.email as string) || null,
      };
    } catch (_error) {
      return {
        isAuthenticated: false,
        email: null,
      };
    }
  }, []);

  useEffect(() => {
    // Hub.listen returns its own unsubscribe function; return it so the listener
    // is torn down on unmount. With fetchCurrentUser now a stable reference the
    // effect subscribes exactly once instead of stacking a new permanent
    // listener on every render.
    const unsubscribe = Hub.listen('auth', ({ payload: { event } }) => {
      switch (event) {
        case 'signedIn':
        case 'signInWithRedirect':
          fetchCurrentUser(true);
          break;
        case 'signedOut':
          // fetchCurrentUser(true);
          break;
        case 'signInWithRedirect_failure':
        case 'tokenRefresh':
          break;
        case 'tokenRefresh_failure':
          logger.warn('[AuthContext] Token refresh failed — user may be silently logged out');
          break;
        case 'customOAuthState':
          break;
        default:
          break;
      }
    });
    return unsubscribe;
  }, [fetchCurrentUser]);

  // checkUserResponse is used to check whether the user has challenges to complete or not.
  const checkUserResponse = useCallback((result: SignInResult) => {
    if (result?.isSignedIn) {
      return {
        success: true,
        message: 'LOGIN SUCCESS',
      };
    }

    const { nextStep } = result;

    if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
      return {
        success: true,
        message: 'MFA_VERIFICATION_REQUIRED',
        navigate: 'mfa-verification',
      };
    }

    if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
      return {
        success: true,
        message: 'MFA_VERIFICATION_REQUIRED',
        navigate: 'sms-mfa-verification',
      };
    }

    if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return {
        success: true,
        message: 'NEW_PASSWORD_REQUIRED',
        navigate: 'set-new-password',
      };
    }

    return null;
  }, []);

  const checkUserCatchError = useCallback((user: string, error: unknown) => {
    const authError = toAuthError(error);
    if (authError.name === 'UserNotConfirmedException') {
      return {
        success: false,
        code: 'UserNotConfirmedException',
        username: user,
        message: authError.message || 'User not confirmed',
      };
    }
    if (authError.name === 'UserNotFoundException') {
      return {
        success: false,
        code: 'UserNotFoundException',
        username: user,
        message: 'Invalid Credentials',
      };
    }
    if (authError.name === 'NotAuthorizedException') {
      return {
        success: false,
        code: 'NotAuthorizedException',
        username: user,
        message: 'Invalid Credentials',
      };
    }
    if (authError.name === 'UserLambdaValidationException') {
      return {
        success: false,
        code: 'UserLambdaValidationException',
        username: user,
        message: 'Invalid Credentials',
      };
    }
    const NETWORK_ERROR_PATTERN = /network\s*error|failed to fetch/i;
    const isNetworkError =
      authError.name === 'NetworkError' || NETWORK_ERROR_PATTERN.test(authError.message ?? '');
    if (isNetworkError) {
      return {
        success: false,
        code: 'NetworkError',
        username: user,
        message: authError.message || 'Network error',
      };
    }
    return {
      success: false,
      message: authError.message || 'LOGIN FAIL',
    };
  }, []);

  const signIn = useCallback(
    async (user: string, password: string) => {
      try {
        setISigningIn(true);
        const organizationUuid = authConfigDetails?.organization_uuid;
        const result = await amplifySignIn({
          username: user,
          password,
          ...(organizationUuid ? { options: { clientMetadata: { organizationUuid } } } : {}),
        });
        setISigningIn(false);
        return checkUserResponse(result) as Result;
      } catch (error) {
        setISigningIn(false);
        return checkUserCatchError(user, error) as Result;
      }
    },
    [authConfigDetails, checkUserResponse, checkUserCatchError],
  );

  const mfaVerification = useCallback(
    async (code: string, _mfaType: string) => {
      try {
        const result = await confirmSignIn({
          challengeResponse: code,
        });
        setTempUserData({});
        if (result?.isSignedIn) {
          // MFA completed the sign-in — mark the post-login window so a backend
          // 401 while loading the destination is surfaced as an unauthorized
          // account (see helper/api.ts) instead of a silent redirect.
          sessionStorage.setItem('postLoginVerification', 'true');
        }
        return checkUserResponse(result) as Result;
      } catch (error) {
        return checkUserCatchError(tempUserData?.attributes?.email || '', error) as Result;
      }
    },
    [tempUserData, checkUserCatchError, checkUserResponse],
  );

  const signOut = useCallback(async () => {
    // Same shape as the forced 401 teardown in src/helper/api.ts, and for the same reason
    // (APX-556): amplifySignOut() is a network call that can reject, and on the old
    // `try { … } catch { return authFailure() }` shape a rejection skipped the ENTIRE
    // teardown and the /login redirect. The user clicked "Log out", nothing visible
    // happened, and they stayed on an authenticated-looking page. The teardown and the
    // redirect therefore live in a `finally`; the caller still gets the failure result.
    let result: { success: boolean; message: string } = { success: true, message: '' };
    try {
      await amplifySignOut();
    } catch (error) {
      // Amplify rejects before clearing its cookies, so wipe the credentials locally or
      // the "sign-out" is only a navigation. See sessionRedirect.clearCognitoCookies.
      sessionRedirect.clearCognitoCookies();
      result = authFailure(error, 'LOGOUT FAIL');
    } finally {
      // FIRST, so nothing added below can ever preempt it (a pathname assignment does not
      // halt synchronous execution, so the rest of the teardown still runs).
      sessionRedirect.toLogin();
      localStorage.removeItem('_jwt');
      sessionStorage?.removeItem('socket_id');
      // Sign-out hard-navigates in the SAME tab, so sessionStorage survives it — clear the
      // ORI-677 welcome-banner session latch(es) so "shown this visit" does not outlive the
      // login and re-show a banner that should be suppressed (AC-1).
      clearAllVendorBannerSessionLatches();
      // Clear the boot fan-out guard so a new session in the same tab (before
      // the hard-nav completes, or if the nav is ever swapped for router.push)
      // still re-runs profile/lookup/views/counter.
      resetUserProfileBootGuard();
      // ORI-654: clear the unresolved-work-type diagnostic throttle on teardown too, matching
      // the boot-guard / welcome-banner "reset paths must stay in sync with auth teardown"
      // contract, so one vendor's throttle history never suppresses the next same-tab session.
      resetWorkTypeDiagnosticsThrottle();
      // ORI-734: clear per-conversation chat drafts (unsent staged files + text) so the
      // outgoing user's selection never restores under the next same-tab session.
      clearAllChatDrafts();
      clearStoredViewConfigs();
    }
    return result;
  }, []);

  const signOutAndClearOnly = useCallback(async () => {
    try {
      await amplifySignOut();
      localStorage.removeItem('_jwt');
      sessionStorage?.removeItem('socket_id');
      // This is an account-switch teardown (a different vendor signs in next, same tab), so
      // clear the ORI-677 session latch here too — matching signOut — or the outgoing
      // account's "shown this visit" leaks into the incoming session (AC-1). No hard nav here,
      // so the shared helper's in-memory latch clear is what actually wipes module scope.
      clearAllVendorBannerSessionLatches();
      // No hard nav here — resetting the module-level boot guard is required
      // so the next login in the same tab re-runs the fan-out.
      resetUserProfileBootGuard();
      // ORI-654: account-switch teardown (same tab), so clear the diagnostic throttle here too.
      resetWorkTypeDiagnosticsThrottle();
      // ORI-734: account-switch teardown (same tab, no hard nav) — clearing the in-memory chat
      // drafts here is load-bearing, or the incoming vendor inherits the outgoing one's staged files.
      clearAllChatDrafts();
      clearStoredViewConfigs();
      return { success: true, message: 'Success' };
    } catch (error) {
      return authFailure(error, 'LOGOUT FAIL');
    }
  }, []);

  const setNewPassword = useCallback(async (newPassword: string) => {
    try {
      // In v6, completeNewPassword is replaced by confirmSignIn
      await confirmSignIn({
        challengeResponse: newPassword,
      });
      setTempUserData({});
      return { success: true, message: 'New password updated successfully' };
    } catch (error) {
      const authError = toAuthError(error);
      // Cognito throws NotAuthorizedException with specific messages for expired sessions.
      const isSessionExpired =
        authError.name === 'NotAuthorizedException' &&
        authError.message?.includes('Invalid session');

      return {
        success: false,
        message: isSessionExpired
          ? 'SESSION_EXPIRED'
          : authError.message || 'Failed to update new password',
      };
    }
  }, []);

  const confirmEmailVerification = useCallback(async (code: string, userId: string) => {
    try {
      await confirmSignUp({
        username: userId,
        confirmationCode: code,
      });
      return { success: true, message: '' };
    } catch (error) {
      return authFailure(error, 'Email verification failed');
    }
  }, []);

  const signup = useCallback(async (username: string, password: string, name: string) => {
    try {
      const result = await signUp({
        username,
        password,
        options: {
          userAttributes: {
            email: username,
            name, // optional
          },
          autoSignIn: true, // In v6, this can be a boolean
        },
      });
      return { success: true, message: '', result };
    } catch (_error) {
      return { success: false, message: 'Sign up failed' };
    }
  }, []);

  const forgotPassword = useCallback(async (userEmail: string) => {
    try {
      await resetPassword({ username: userEmail });
      return { success: true, message: '' };
    } catch (error) {
      return authFailure(error, 'Forgot password failed');
    }
  }, []);

  const forgotPasswordSubmit = useCallback(
    async (userEmail: string, code: string, newPassword: string) => {
      try {
        await confirmResetPassword({
          username: userEmail,
          confirmationCode: code,
          newPassword,
        });
        return { success: true, message: '' };
      } catch (error) {
        return authFailure(error, 'Forgot password failed');
      }
    },
    [],
  );

  const setupMfa = useCallback(async () => {
    try {
      const result = await setUpTOTP();
      return {
        success: true,
        message: 'Hello',
        code: result.getSetupUri('AWSCognito').toString(),
      };
    } catch (error) {
      const authError = toAuthError(error);
      return {
        success: false,
        message: authError.message || 'Setup MFA failed',
        code: authError.code,
      };
    }
  }, []);

  const verifyTOTP = useCallback(async (code: string) => {
    try {
      await verifyTOTPSetup({ code });
      await updateMFAPreference({ totp: 'PREFERRED' });
      return { success: true, message: '' };
    } catch (error) {
      return authFailure(error, 'Setup MFA failed');
    }
  }, []);

  const getCurrentMFA = useCallback(async () => {
    try {
      const result = await fetchMFAPreference();
      // Convert the result to match the old format
      let mfaType = 'NOMFA';
      if (result.preferred === 'TOTP') {
        mfaType = 'SOFTWARE_TOKEN_MFA';
      } else if (result.preferred === 'SMS') {
        mfaType = 'SMS_MFA';
      }
      return { success: true, message: '', code: mfaType };
    } catch (error) {
      return authFailure(error, 'Something went wrong');
    }
  }, []);

  // Laravel endpoints
  const forgotPasswordAPI = useCallback(
    async (email: string) => {
      try {
        const hostName = typeof window !== 'undefined' ? window.location.hostname : '';
        const payload = { email, client_key: hostName };
        await dispatch(authForgotPassword()(payload)).unwrap();
        return { success: true, message: '' };
      } catch (error) {
        const authError = toAuthError(error);
        if (Number(authError.status) === 429) {
          return {
            success: false,
            message: authError.message || 'Too Many Attempts.',
            rateLimited: true,
            retryAfter: typeof authError.retry_after === 'number' ? authError.retry_after : null,
          };
        }
        return authFailure(error, 'Forgot password failed');
      }
    },
    [dispatch],
  );

  const checkSignedUrlAPI = useCallback(
    async (verifyId: string, expires: number, signature: string) => {
      try {
        await dispatch(authCheckSignedUrl(verifyId, expires, signature)({})).unwrap();
        return { success: true, message: 'Valid signed URL' };
      } catch (error) {
        const authError = toAuthError(error);
        if (authError.status === 400 || authError.status === 422) {
          return {
            success: false,
            message: authError.message || 'The signed URL has expired',
          };
        }
        return {
          success: false,
          message: authError.message || 'Internal server error',
        };
      }
    },
    [dispatch],
  );

  const resetPasswordAPI = useCallback(
    async (password: string, verifyId: string, expires: number, signature: string) => {
      try {
        const hostName = typeof window !== 'undefined' ? window.location.hostname : '';
        const signedUrl = `${getBaseUrl()}api/public/verify/${verifyId}?expires=${expires}&signature=${signature}`;

        const payload = { password, signedUrl, client_key: hostName };
        const response = await dispatch(authResetPassword()(payload)).unwrap();

        if (response.status === 400 || response.status === 422) {
          throw new Error('The signed URL has expired');
        }

        if (response.status === 500) {
          throw new Error('Internal server error');
        }

        return { success: true, message: 'Password reset successfully' };
      } catch (error) {
        return authFailure(error, 'Password reset failed');
      }
    },
    [dispatch],
  );

  //  Add theme class to body
  const addThemeClassToBody = useCallback((colorTheme: string) => {
    document.body.className = colorTheme;
  }, []);

  const triggerThemeChange = useCallback(
    async (name: string, template: string) => {
      const accessToken = await getAccessToken();

      dispatch(updateTemplate(accessToken)({ name, template }))
        .unwrap()
        .then(() => {
          setTheme(template);
          addThemeClassToBody(template);
        });
    },
    [dispatch, addThemeClassToBody],
  );

  useEffect(() => {
    const colorTheme = localStorage.getItem('template') || 'default';
    addThemeClassToBody(colorTheme);
  }, [addThemeClassToBody]);

  useEffect(() => {
    if (theme) {
      addThemeClassToBody(theme);
      localStorage.setItem('template', theme);
    }
  }, [theme, addThemeClassToBody]);

  // Memoize the exposed context value so consumers get a stable object identity
  // across re-renders that don't touch auth state. Every function above is a
  // stable useCallback reference, so this only changes when the auth state
  // values do — keeping effect deps like checkAuth from re-firing on no-op
  // re-renders.
  return useMemo(
    () => ({
      isRequesting,
      signIn,
      signOut,
      signOutAndClearOnly,
      setNewPassword,
      confirmEmailVerification,
      forgotPassword,
      forgotPasswordSubmit,
      authConfigDetails,
      isAuthInitialized,
      isSigningIn,
      checkAuth,
      mfaVerification,
      setupMfa,
      verifyTOTP,
      getCurrentMFA,
      triggerThemeChange,
      signup,
      domainNotValid,
      // Laravel
      forgotPasswordAPI,
      checkSignedUrlAPI,
      resetPasswordAPI,
    }),
    [
      isRequesting,
      signIn,
      signOut,
      signOutAndClearOnly,
      setNewPassword,
      confirmEmailVerification,
      forgotPassword,
      forgotPasswordSubmit,
      authConfigDetails,
      isAuthInitialized,
      isSigningIn,
      checkAuth,
      mfaVerification,
      setupMfa,
      verifyTOTP,
      getCurrentMFA,
      triggerThemeChange,
      signup,
      domainNotValid,
      forgotPasswordAPI,
      checkSignedUrlAPI,
      resetPasswordAPI,
    ],
  );
};

export const ProvideAuth: React.FC<Props> = ({ children = null, initialAuthConfig = null }) => {
  const auth = useProvideAuth(initialAuthConfig);
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
